/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import { useCallback } from 'react';
import type { CityStats, NewsItem, RandomEvent } from '../types';
import { resolvePlayAudio } from '../src/adapters/ui/hooks/useAudio';
import {
  resolveExecuteSocAction,
  type SocActionType,
} from '../src/adapters/ui/hooks/useSocMutations';

export interface UseSocHandlersDeps {
  stats: CityStats;
  setStats: React.Dispatch<React.SetStateAction<CityStats>>;
  hasJumpGate: boolean;
  activeEvent: RandomEvent | null;
  setActiveEvent: React.Dispatch<React.SetStateAction<RandomEvent | null>>;
  addNewsItem: (item: NewsItem) => void;
}

export function useSocHandlers({
  stats,
  setStats,
  hasJumpGate,
  activeEvent,
  setActiveEvent,
  addNewsItem,
}: UseSocHandlersDeps) {
  const handleSOCAction = useCallback(
    (action: SocActionType) => {
      const result = resolveExecuteSocAction({
        action,
        stats: { budget: stats.budget, threat: stats.threat },
        hasJumpGate,
        activeEventId: activeEvent?.id ?? null,
      });

      if (result.newsMessage) {
        addNewsItem({
          id: Date.now().toString(),
          text: result.newsMessage,
          type: result.newsType ?? 'neutral',
        });
      }

      if (!result.ok) {
        if (result.playErrorSfx) {
          resolvePlayAudio({ type: 'sfx', sound: 'error' });
        }
        return;
      }

      if (result.stats) {
        setStats((prev) => ({
          ...prev,
          budget: result.stats!.budget,
          threat: result.stats!.threat,
        }));
      }

      if (result.clearActiveEvent) {
        setActiveEvent(null);
      }
    },
    [
      activeEvent?.id,
      addNewsItem,
      hasJumpGate,
      setActiveEvent,
      setStats,
      stats.budget,
      stats.threat,
    ],
  );

  return { handleSOCAction };
}